import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getToken() {
  return localStorage.getItem('token');
}

async function api(endpoint, options = {}) {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`);
  }
  return response.json();
}

export function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  const loadEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      const data = await api(`/api/calendar/events?start_date=${startDate}&end_date=${endDate}`);
      setEvents(data || []);
    } catch (error) {
      toast.error('Failed to load calendar events');
      setEvents([]);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const getEventsForDate = (day) => {
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      .toISOString()
      .split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const getEventBadgeColor = (type, status) => {
    if (type === 'work_order') {
      if (status === 'completed') return 'bg-green-100 text-green-800 border-green-300';
      if (status === 'in_progress') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      return 'bg-blue-100 text-blue-800 border-blue-300';
    }
    if (type === 'walkthrough') {
      return 'bg-purple-100 text-purple-800 border-purple-300';
    }
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Calendar</h1>
        <p className="text-muted-foreground">View and manage your schedule</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl">{monthName}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-button">
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={previousMonth} data-testid="prev-month-button">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month-button">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1">
              {/* Day Headers */}
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-semibold text-muted-foreground p-2">
                  {day}
                </div>
              ))}
              
              {/* Empty cells for days before month starts */}
              {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square p-1" />
              ))}
              
              {/* Calendar days */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dayEvents = getEventsForDate(day);
                const today = isToday(day);
                
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDate(day)}
                    className={`aspect-square p-1 rounded-lg border transition-colors text-left relative ${
                      today ? 'border-primary bg-primary/5 font-bold' : 'border-transparent hover:bg-secondary'
                    } ${selectedDate === day ? 'bg-secondary ring-2 ring-primary' : ''}`}
                    data-testid={`calendar-day-${day}`}
                  >
                    <div className="text-sm">{day}</div>
                    {dayEvents.length > 0 && (
                      <div className="absolute bottom-1 left-1 right-1 flex gap-0.5">
                        {dayEvents.slice(0, 3).map((event, idx) => (
                          <div
                            key={idx}
                            className={`h-1 flex-1 rounded ${
                              event.type === 'work_order' ? 'bg-blue-500' : 'bg-purple-500'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Events */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              {selectedDate ? `${monthName.split(' ')[0]} ${selectedDate}` : 'Select a date'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDate && (
              <div className="text-center text-muted-foreground py-8">
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Click a date to view events</p>
              </div>
            )}
            {selectedDate && selectedDateEvents.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                <p className="text-sm">No events scheduled</p>
              </div>
            )}
            {selectedDate && selectedDateEvents.length > 0 && (
              <div className="space-y-3">
                {selectedDateEvents.map((event) => (
                  <div
                    key={event.id}
                    className={`p-3 rounded-lg border ${getEventBadgeColor(event.type, event.status)}`}
                    data-testid="calendar-event"
                  >
                    <div className="font-medium text-sm mb-1">{event.title}</div>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span className="capitalize">{event.status?.replace('_', ' ')}</span>
                      </div>
                      {event.assigned_to && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{event.assigned_to}</span>
                        </div>
                      )}
                      <Badge variant="outline" className="w-fit text-xs mt-1">
                        {event.type === 'work_order' ? 'Work Order' : 'Walkthrough'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Events (Next 7 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {events
              .filter(event => {
                const eventDate = new Date(event.date);
                const today = new Date();
                const weekFromNow = new Date();
                weekFromNow.setDate(today.getDate() + 7);
                return eventDate >= today && eventDate <= weekFromNow;
              })
              .slice(0, 6)
              .map((event) => (
                <div
                  key={event.id}
                  className="p-3 rounded-lg border bg-card hover:shadow-md transition-shadow"
                  data-testid="upcoming-event"
                >
                  <div className="font-medium text-sm mb-1">{event.title}</div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CalendarIcon className="h-3 w-3" />
                    <span>{new Date(event.date).toLocaleDateString()}</span>
                  </div>
                  <Badge variant="outline" className="w-fit text-xs mt-2">
                    {event.type === 'work_order' ? 'Work Order' : 'Walkthrough'}
                  </Badge>
                </div>
              ))}
            {events.filter(event => {
              const eventDate = new Date(event.date);
              const today = new Date();
              const weekFromNow = new Date();
              weekFromNow.setDate(today.getDate() + 7);
              return eventDate >= today && eventDate <= weekFromNow;
            }).length === 0 && (
              <div className="col-span-full text-center text-muted-foreground py-8">
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No upcoming events in the next 7 days</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
